import { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Code,
} from "@chakra-ui/react";
import { useApi } from "../../hooks/useApi";
import { DocDefParse } from "../../types";

// ─── Single-regex variant props ────────────────────────────────────────────────
type SingleProps = {
  variant?: "single";
  fieldKey: string;
  onSave: (regexString: string, dateFunction?: string) => Promise<void>;
  onClose: () => void;
  showDateFunction?: boolean;
  initialValue?: string | null;
  initialDateFunction?: string | null;
  documentText?: string;
};

// ─── Multi-regex variant props ─────────────────────────────────────────────────
type MultiProps = {
  variant: "multi";
  fieldKey: string;
  keyId: string;
  keyName: string;
  onClose: () => void;
  documentText?: string;
};

type RegexTesterProps = SingleProps | MultiProps;

// ─── Test result types ─────────────────────────────────────────────────────────
type TestResult =
  | { kind: "error"; message: string }
  | { kind: "pass"; lines: string[] }
  | { kind: "fail"; lines: string[]; expected: string[]; actual: string[] }
  | { kind: "info"; lines: string[] }
  | { kind: "nomatch"; testString: string };

const EMPTY_ROW: Omit<DocDefParse, "sk" | "key_id" | "key_name" | "created"> = {
  score: 10,
  regex: "",
  mode: "FIND",
  key_function: "get_string:1",
  tag: null,
  note: null,
};

// ─── Shared inline test panel ──────────────────────────────────────────────────
function InlineTestPanel({
  regex,
  onRegexChange,
  defaultTestString,
}: {
  regex: string;
  onRegexChange?: (v: string) => void;
  defaultTestString?: string;
}) {
  const [testString, setTestString] = useState("");
  const [requiredResult, setRequiredResult] = useState("");
  const [result, setResult] = useState<TestResult | null>(null);

  const handleTest = () => {
    if (!regex) {
      setResult({ kind: "error", message: "Enter a regex pattern." });
      return;
    }
    let re: RegExp;
    try {
      re = new RegExp(regex, "gi");
    } catch {
      setResult({ kind: "error", message: "Invalid regex pattern." });
      return;
    }
    const effectiveTestString = testString.trim() ? testString : (defaultTestString ?? "");
    const matches = [...effectiveTestString.matchAll(re)];
    if (matches.length === 0) {
      setResult({ kind: "nomatch", testString: effectiveTestString });
      return;
    }
    const lines: string[] = [`${matches.length} match(es):`];
    matches.forEach((m, i) => {
      lines.push(`[${i}] "${m[0]}" at index ${m.index}`);
      m.slice(1).forEach((g, gi) => lines.push(`  group ${gi}: ${g ?? "(undefined)"}`));
    });
    if (!requiredResult.trim()) {
      setResult({ kind: "info", lines });
      return;
    }
    const firstMatch = matches[0];
    const expectedParts = [...requiredResult.matchAll(/\(([^)]*)\)/g)].map((m) => m[1]);
    const hasGroups = firstMatch.length > 1;
    const actual = hasGroups ? firstMatch.slice(1).map((g) => g ?? "") : [firstMatch[0]];
    const passed =
      actual.length === expectedParts.length && actual.every((v, i) => v === expectedParts[i]);
    if (passed) setResult({ kind: "pass", lines });
    else setResult({ kind: "fail", lines, expected: expectedParts, actual });
  };

  const bgColor = () => {
    if (!result) return undefined;
    if (result.kind === "error" || result.kind === "nomatch") return "red.50";
    if (result.kind === "pass") return "green.50";
    if (result.kind === "fail") return "orange.50";
    return "gray.50";
  };
  const textColor = () => {
    if (!result) return undefined;
    if (result.kind === "error" || result.kind === "nomatch") return "red.700";
    if (result.kind === "pass") return "green.700";
    if (result.kind === "fail") return "orange.700";
    return "gray.700";
  };

  return (
    <VStack spacing={2} align="stretch">
      {onRegexChange && (
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>Regex Pattern</FormLabel>
          <Input
            size="sm"
            value={regex}
            onChange={(e) => onRegexChange(e.target.value)}
            fontFamily="mono"
            bg="white"
          />
        </FormControl>
      )}
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>Test String</FormLabel>
        <Input
          size="sm"
          placeholder={defaultTestString ? "Leave blank to use the whole document text" : "Text to test against"}
          value={testString}
          onChange={(e) => setTestString(e.target.value)}
          bg="white"
        />
        {defaultTestString && (
          <Text fontSize="xs" color="gray.500" mt={1}>
            Leave blank to test against the whole document text.
          </Text>
        )}
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>
          Required Result{" "}
          <Box as="span" fontWeight="normal" color="gray.500">(wrap each group in parentheses)</Box>
        </FormLabel>
        <Input
          size="sm"
          placeholder="e.g. (Invoice date:)(19)(March)(2025)"
          value={requiredResult}
          onChange={(e) => setRequiredResult(e.target.value)}
          fontFamily="mono"
          bg="white"
        />
      </FormControl>
      <Button size="xs" onClick={handleTest}>Test</Button>
      {result !== null && (
        <Box>
          {(result.kind === "pass" || result.kind === "fail" || result.kind === "nomatch") && (
            <Text fontWeight="bold" fontSize="sm" mb={1} color={result.kind === "pass" ? "green.600" : result.kind === "nomatch" ? "red.600" : "orange.600"}>
              {result.kind === "pass" ? "PASS" : result.kind === "nomatch" ? "NO MATCH" : "FAIL"}
            </Text>
          )}
          {result.kind === "fail" && (
            <Code display="block" whiteSpace="pre-wrap" p={2} fontSize="xs" borderRadius="md" bg="orange.50" color="orange.700" mb={1}>
              {`Expected: ${result.expected.join(" | ")}\nActual:   ${result.actual.join(" | ")}`}
            </Code>
          )}
          <Code display="block" whiteSpace="pre-wrap" p={2} fontSize="xs" borderRadius="md" bg={bgColor()} color={textColor()}>
            {result.kind === "error"
              ? result.message
              : result.kind === "nomatch"
              ? `Regex did not match anything in:\n"${result.testString}"`
              : result.lines.join("\n")}
          </Code>
        </Box>
      )}
    </VStack>
  );
}

// ─── Single-regex variant ──────────────────────────────────────────────────────
function SingleRegexTester({ fieldKey, onSave, onClose, showDateFunction, initialValue, initialDateFunction, documentText }: SingleProps) {
  const [regexString, setRegexString] = useState(initialValue ?? "");
  const [dateFormat, setDateFormat] = useState<"dmy" | "ymd">(
    initialDateFunction === "get_date:1,2,3" ? "ymd" : "dmy"
  );

  const handleSave = () => {
    const dateFunction = showDateFunction
      ? (dateFormat === "dmy" ? "get_date:3,2,1" : "get_date:1,2,3")
      : undefined;
    onSave(regexString, dateFunction);
  };

  return (
    <Box mt={2} p={3} border="1px" borderColor="blue.200" borderRadius="md" bg="blue.50">
      <Text fontWeight="semibold" fontSize="xs" color="blue.700" mb={2}>
        Regex Tester — {fieldKey}
      </Text>
      <VStack spacing={2} align="stretch">
        <InlineTestPanel regex={regexString} onRegexChange={setRegexString} defaultTestString={documentText} />
        {showDateFunction && (
          <FormControl>
            <FormLabel fontSize="xs" mb={1}>Date Format</FormLabel>
            <RadioGroup value={dateFormat} onChange={(v) => setDateFormat(v as "dmy" | "ymd")}>
              <HStack spacing={4}>
                <Radio value="dmy" size="sm">Day Month Year</Radio>
                <Radio value="ymd" size="sm">Year Month Day</Radio>
              </HStack>
            </RadioGroup>
          </FormControl>
        )}
        <HStack>
          <Button size="xs" colorScheme="blue" onClick={handleSave}>Save</Button>
          <Button size="xs" variant="ghost" onClick={onClose}>Close</Button>
        </HStack>
      </VStack>
    </Box>
  );
}

// ─── Multi-regex variant ───────────────────────────────────────────────────────
function MultiRegexTester({ fieldKey, keyId, keyName, onClose, documentText }: MultiProps) {
  const { getDocDefParse, putDocDefParse, deleteDocDefParse } = useApi();

  const [rules, setRules] = useState<DocDefParse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingRow, setEditingRow] = useState<Partial<DocDefParse> & { isNew?: boolean } | null>(null);
  const [testingRow, setTestingRow] = useState<DocDefParse | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadRules = async () => {
    if (!keyId) {
      setRules([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await getDocDefParse(keyId, keyName);
      setRules(data);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, [keyId, keyName]);

  const startAdd = () => {
    setTestingRow(null);
    setEditingRow({ ...EMPTY_ROW, key_id: keyId, key_name: keyName, isNew: true });
  };

  const startEdit = (rule: DocDefParse) => {
    setTestingRow(null);
    setEditingRow({ ...rule, isNew: false });
  };

  const cancelEdit = () => setEditingRow(null);

  const saveRow = async () => {
    if (!editingRow) return;
    setIsSaving(true);
    try {
      await putDocDefParse({
        sk: editingRow.isNew ? undefined : editingRow.sk,
        key_id: keyId,
        key_name: keyName,
        score: editingRow.score ?? 10,
        regex: editingRow.regex ?? "",
        mode: editingRow.mode ?? "FIND",
        key_function: editingRow.key_function ?? "get_string:1",
        tag: editingRow.tag ?? null,
        note: editingRow.note ?? null,
      });
      setEditingRow(null);
      await loadRules();
    } finally {
      setIsSaving(false);
    }
  };

  const deleteRow = async (sk: number) => {
    await deleteDocDefParse(sk);
    await loadRules();
  };

  return (
    <Box mt={2} p={3} border="1px" borderColor="blue.200" borderRadius="md" bg="blue.50">
      <HStack justify="space-between" mb={2}>
        <Text fontWeight="semibold" fontSize="xs" color="blue.700">
          Parse Rules — {fieldKey} {keyId ? `(${keyId})` : "(no key_id)"}
        </Text>
        <Button size="xs" variant="ghost" onClick={onClose}>Close</Button>
      </HStack>

      {!keyId && (
        <Text fontSize="xs" color="orange.600" mb={2}>
          Set Doc From on the file to edit parse rules.
        </Text>
      )}

      {isLoading ? (
        <Spinner size="sm" />
      ) : (
        <>
          {rules.length > 0 && (
            <Box overflowX="auto" mb={2}>
              <Table size="xs" variant="simple" bg="white" borderRadius="md">
                <Thead>
                  <Tr>
                    <Th fontSize="xs">Score</Th>
                    <Th fontSize="xs">Regex</Th>
                    <Th fontSize="xs">Function</Th>
                    <Th fontSize="xs"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rules.map((rule) => (
                    <>
                      <Tr key={rule.sk}>
                        <Td fontSize="xs">{rule.score}</Td>
                        <Td fontSize="xs" fontFamily="mono" maxW="160px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={rule.regex}>
                          {rule.regex}
                        </Td>
                        <Td fontSize="xs">{rule.key_function}</Td>
                        <Td fontSize="xs">
                          <HStack spacing={1}>
                            <Button size="xs" variant="ghost" onClick={() => setTestingRow(testingRow?.sk === rule.sk ? null : rule)}>
                              Test
                            </Button>
                            <Button size="xs" variant="ghost" onClick={() => startEdit(rule)}>
                              Edit
                            </Button>
                            <Button size="xs" variant="ghost" colorScheme="red" onClick={() => rule.sk !== undefined && deleteRow(rule.sk)}>
                              Del
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                      {testingRow?.sk === rule.sk && (
                        <Tr key={`test-${rule.sk}`}>
                          <Td colSpan={4} bg="gray.50" p={2}>
                            <InlineTestPanel regex={rule.regex} defaultTestString={documentText} />
                          </Td>
                        </Tr>
                      )}
                    </>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          {rules.length === 0 && !editingRow && (
            <Text fontSize="xs" color="gray.500" mb={2}>No rules yet.</Text>
          )}

          {editingRow ? (
            <Box bg="white" p={2} borderRadius="md" border="1px" borderColor="blue.100">
              <Text fontSize="xs" fontWeight="semibold" mb={2} color="blue.700">
                {editingRow.isNew ? "Add Rule" : "Edit Rule"}
              </Text>
              <VStack spacing={2} align="stretch">
                <HStack>
                  <FormControl flex="0 0 60px">
                    <FormLabel fontSize="xs" mb={1}>Score</FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      value={editingRow.score ?? ""}
                      onChange={(e) => setEditingRow((r) => r ? { ...r, score: parseInt(e.target.value) || 0 } : r)}
                    />
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel fontSize="xs" mb={1}>Function</FormLabel>
                    <Input
                      size="sm"
                      fontFamily="mono"
                      value={editingRow.key_function ?? ""}
                      onChange={(e) => setEditingRow((r) => r ? { ...r, key_function: e.target.value } : r)}
                    />
                  </FormControl>
                </HStack>
                <InlineTestPanel
                  regex={editingRow.regex ?? ""}
                  onRegexChange={(v) => setEditingRow((r) => r ? { ...r, regex: v } : r)}
                  defaultTestString={documentText}
                />
                <FormControl>
                  <FormLabel fontSize="xs" mb={1}>Note</FormLabel>
                  <Input
                    size="sm"
                    value={editingRow.note ?? ""}
                    onChange={(e) => setEditingRow((r) => r ? { ...r, note: e.target.value } : r)}
                  />
                </FormControl>
                <HStack>
                  <Button size="xs" colorScheme="blue" onClick={saveRow} isLoading={isSaving}>Save</Button>
                  <Button size="xs" variant="ghost" onClick={cancelEdit}>Cancel</Button>
                </HStack>
              </VStack>
            </Box>
          ) : (
            keyId && <Button size="xs" onClick={startAdd}>+ Add Rule</Button>
          )}
        </>
      )}
    </Box>
  );
}

// ─── Public component ──────────────────────────────────────────────────────────
export default function RegexTester(props: RegexTesterProps) {
  if (props.variant === "multi") return <MultiRegexTester {...props} />;
  return <SingleRegexTester {...(props as SingleProps)} />;
}
